import keyBy from 'lodash/keyBy'
import groupBy from 'lodash/groupBy'
import get from 'lodash/get'
import orderBy from 'lodash/orderBy'
import flatMap from 'lodash/flatMap'
import partition from 'lodash/partition'
import { formatDate } from '../../../../van-shared/utils/utils'

export const TRANSACTION_TYPES = {
  INVOICE: 'invoice',
  INSTALLMENT: 'installment'
}

export const PAYMENT_CHOICE = {
  PREPAID: 'prepaid',
  POSTPAID: 'postpaid',
  POD: 'pay_on_delivery',
  CONSIGNMENT: 'consignment',
  CREDIT: 'credit'
}

export const PAYMENT_STATUS = {
  DUE: 'due',
  OVERDUE: 'overdue',
  PAID: 'paid',
  CONVERTING: 'converting' // invoices that will be converted to payment plans
}

const PAYMENT_PLAN_FREQUENCY_DAYS = {
  WEEKLY: 7,
  BIWEEKLY: 14,
  FIFTEEN_DAYS: 15,
  MONTHLY: 30,
  FORTY_FIVE_DAYS: 45,
  BIMONTHLY: 60,
  TRIMONTHLY: 90
}

const getTransactionStatus = (transaction) => {
  if (!transaction) return null
  if (isTransactionConverting(transaction)) return PAYMENT_STATUS.CONVERTING
  if (isTransactionPaid(transaction)) return PAYMENT_STATUS.PAID

  const today = formatDate(new Date(), 'snapshotId')
  const dueDate = formatDate(transaction.dueDate, 'snapshotId')
  if (dueDate === today) return PAYMENT_STATUS.DUE
  if (dueDate < today) return PAYMENT_STATUS.OVERDUE
  return null
}

const isTransactionConverting = (transaction) => {
  return !!get(transaction, 'isConverting')
}

const isPODTransaction = (transaction) => {
  const paymentChoice = get(transaction, 'paymentChoice', '')
  return paymentChoice === PAYMENT_CHOICE.POD
}

// Allow POD payment if the transaction is POD or is flagged as converting
export const canMakePODPayment = (transaction) => {
  return isPODTransaction(transaction) || isTransactionConverting(transaction)
}

const isTransactionPaid = (transaction) => {
  // Ensure balance is never negative (handles cases where users overpay).
  const balance = Math.max(get(transaction, 'balance', 0), 0)
  return balance === 0
}

const getPartiallyPaidAmount = (transaction) => {
  if (isTransactionPaid(transaction)) {
    return 0
  }
  return get(transaction, 'amount', 0) - get(transaction, 'balance', 0)
}

const isPaymentPending = (paymentsPending, transaction, matcherFn) => {
  if (!paymentsPending || !paymentsPending.length || isTransactionPaid(transaction)) {
    return false
  }

  return paymentsPending.some(matcherFn)
}

const isInvoicePaymentPending = (paymentsPending, invoice) => {
  const balance = get(invoice, 'balance', 0)
  return isPaymentPending(paymentsPending, invoice, (payment) =>
    payment.appliedAmount === balance
  )
}

const isInstallmentPaymentPending = (paymentsPending, installment) => {
  const balance = get(installment, 'balance', 0)
  const installmentNo = get(installment, 'installmentNo', '')
  return isPaymentPending(paymentsPending, installment, (payment) =>
    payment.appliedAmount === balance && payment.installmentNo === installmentNo
  )
}

export const isInvoiceTransaction = (transaction) => {
  return get(transaction, 'txnType') === TRANSACTION_TYPES.INVOICE
}

export const isInstallmentTransaction = (transaction) => {
  return get(transaction, 'txnType') === TRANSACTION_TYPES.INSTALLMENT
}

export const getNumInstallments = (transaction) => {
  return get(transaction, 'paymentPlanData.numInstallments')
}

export const isFixedTermInstallment = (transaction) => {
  return getNumInstallments(transaction) === 1
}

/**
 * Builds and returns payment-related event objects for a transaction.
 * Supports both single and multiple payment scenarios.
 *
 * For multiple payments, events are *unshifted* instead of *pushed*,
 * so the earliest payments appear at the bottom of the timeline
 * (older first, newer last when rendered top-down).
 */
const getPaymentEvents = (transaction, options = {}) => {
  const paymentsData = get(transaction, 'paymentsData')
  if (!paymentsData || !paymentsData.length) return null

  const getPaidAmount = options.getPaidAmount || ((payment) => get(payment, 'amount', 0))
  const isPaid = get(transaction, 'isPaid')
  const amount = get(transaction, 'amount', 0)
  const description = `For ${getPayableLabel(transaction)}` // TODO: Replace when payment channel is available

  // Handle a single payment record simply — no need to build an array for one entry.
  if (paymentsData.length === 1) {
    const payment = paymentsData[0]
    const amountPaid = getPaidAmount(payment)
    const date = get(payment, 'txnDate')
    const event = {
      date,
      label: 'Partial payment',
      amount: amountPaid,
      description,
      balance: amount - amountPaid
    }

    if (isPaid) {
      event.label = 'Payment'
      event.balance = 0
    }
    return event
  } else {
    const events = []
    let amountToPay = amount

    for (let i = 0; i < paymentsData.length; i++) {
      const payment = paymentsData[i]

      const date = get(payment, 'txnDate')
      const amountPaid = getPaidAmount(payment)
      const balance = amountToPay - amountPaid
      const label = balance ? 'Partial payment' : 'Payment'

      // We use `unshift` (prepend) instead of `push` (append)
      // so that the *earliest* payments appear lower in the list (bottom of the card).
      events.unshift({
        date,
        label,
        amount: amountPaid,
        description,
        balance
      })
      amountToPay = balance
    }
    return events
  }
}

const getInvoicePaymentEvent = (transaction) =>
  getPaymentEvents(transaction, {
    getPaidAmount: payment => get(payment, 'amount', 0)
  })

const getInstallmentPaymentEvent = (transaction) =>
  getPaymentEvents(transaction, {
    getPaidAmount: payment => get(payment, 'appliedAmount', 0)
  })

const getTransactionInstallmentEvent = (transaction) => {
  if (isInvoiceTransaction(transaction) || isTransactionConverting(transaction)) {
    return null
  }

  const amount = get(transaction, 'amount', 0)
  const installmentNo = get(transaction, 'installmentNo')
  const numInstallments = getNumInstallments(transaction)
  let label = `Instalment ${installmentNo} of ${numInstallments} created`

  if (isFixedTermInstallment(transaction)) {
    const frequency = get(transaction, 'paymentPlanData.frequency')
    label = `Fixed-term-${PAYMENT_PLAN_FREQUENCY_DAYS[frequency]} created`
  }

  return {
    date: get(transaction, 'txnDate'),
    label,
    amount,
    description: (amount * numInstallments),
    balance: amount
  }
}

const getTransactionIssuedEvent = (transaction) => {
  const shipmentId = get(transaction, 'shipmentData.shipmentId')
  const shipmentNo = shipmentId ? shipmentId.split(':shipmentNo:')[1] : ''
  const isInstallment = isInstallmentTransaction(transaction)
  const amount = get(transaction, `${isInstallment ? 'invoiceData.amount' : 'amount'}`, 0)
  const isLateFee = get(transaction, 'isLateFee')
  const isServiceFee = get(transaction, 'isServiceFee')

  let description = `For delivery #${shipmentNo}`
  if (isLateFee) {
    description = 'late fee(s)'
  }
  if (isServiceFee) {
    const invoiceDocNo = get(transaction, 'docNo')
    const invoiceDocSuffix = invoiceDocNo.slice(-4)
    description = `For service fee ${invoiceDocSuffix}`
  }

  return {
    date: get(transaction, `${isInstallment ? 'invoiceData.txnDate' : 'txnDate'}`),
    label: 'Invoice issued',
    amount,
    description,
    balance: amount
  }
}

/**
 * Adds timeline events to a transaction object.
 * The goal is to group related transaction events (e.g., issued, payments, installments)
 * and return them in a structure suitable for chronological display.
 */
const addTransactionEvents = (transaction) => {
  // Previously, we initialized the events array with the two base transaction events:
  // getTransactionInstallmentEvent(transaction),
  // getTransactionIssuedEvent(transaction)
  //
  // However, that caused them to appear *at the top* of the UI card if the dates are the same.
  // We now initialize an empty array and push these base events *after* all payment-related ones,
  // so they appear *at the bottom* of the card (more natural chronological order).
  const events = []

  // Add conditional payment-related events based on transaction type.
  if (isInvoiceTransaction(transaction)) {
    events.push(getInvoicePaymentEvent(transaction))
  }
  if (isInstallmentTransaction(transaction)) {
    events.push(getInstallmentPaymentEvent(transaction))
  }

  // Only add "issued" event if we have either invoiceData or shipmentData.
  // Rationale: installments created from manual payment plans (no invoice)
  // should not show an "Invoice issued" timeline entry because there is nothing to reference.
  // add the install/issued events only when appropriate
  const installmentEvent = getTransactionInstallmentEvent(transaction)
  const issuedEvent = hasInvoiceOrShipment(transaction) ? getTransactionIssuedEvent(transaction) : null

  if (installmentEvent) {
    events.push(installmentEvent)
  }

  // Add base transaction lifecycle events *last*,
  // ensuring they render below payment or installment updates in the timeline.
  if (issuedEvent) {
    events.push(issuedEvent)
  }

  // Return a new transaction object including grouped events by date for clean display.
  return {
    ...transaction,
    events: groupEventsByDate(events)
  }
}

const hasInvoiceOrShipment = (transaction) => {
  return !!get(transaction, 'invoiceData') || !!get(transaction, 'shipmentData')
}

/**
 * Flattens and removes nullish values from a mixed array of objects, arrays, or nulls.
 *
 * @example
 * const raw = [{ a: 1 }, null, [{ b: 2 }, { c: 3 }], undefined];
 * const cleaned = flattenAndCompact(raw);
 * // cleaned = [{ a: 1 }, { b: 2 }, { c: 3 }]
 */
const flattenAndCompact = (items) => {
  return flatMap(items, item => {
    if (!item) return []
    if (Array.isArray(item)) return item
    return [item]
  })
}

// Group events by date
const groupEventsByDate = (events) => {
  const dateField = 'date'
  const cleanedEvents = flattenAndCompact(events)

  // Sort all events by date (ascending)
  const sortedEvents = orderBy(
    cleanedEvents,
    [event => new Date(get(event, dateField))],
    ['asc']
  )

  // Group events by formatted date (e.g. '2025-07-29')
  const groupedEvents = groupBy(sortedEvents, event =>
    formatDate(get(event, dateField), 'snapshotId')
  )

  // Convert to array of { date, items }, ordered so oldest dates come last
  return orderBy(
    Object.entries(groupedEvents).map(([date, items]) => ({ date, items })),
    [entry => new Date(entry.date)],
    ['desc']
  )
}

/**
 * Checks if the given invoiceId is a Late Fee invoice.
 * Both maps (invoiceLateFees and installmentLateFees) are keyed by the fee invoice ID.
 */
const isLateFeeInvoice = (invoiceId, invoiceLateFees, installmentLateFees) => {
  return !!(invoiceLateFees[invoiceId] || installmentLateFees[invoiceId])
}

/**
 * Checks if the given invoiceId is a Service Fee invoice.
 * Map (paymentPlanServiceFees) is keyed by the fee invoice ID.
 */
const isServiceFeeInvoice = (invoiceId, paymentPlanServiceFees) => {
  return !!paymentPlanServiceFees[invoiceId]
}

// Normalize payment choice for business logic:
// - Treat CONSIGNMENT and POSTPAID as POD until clearer distinctions are defined.
// - Treat CREDIT as POD only when the invoice has already been paid (i.e. CREDIT but payment was completed).
// const normalizePaymentChoice = (choice, isPaid) => {
const normalizePaymentChoice = (choice, isInvoicePaid) => {
  const isConsignmentOrPostpaid = (
    [PAYMENT_CHOICE.CONSIGNMENT, PAYMENT_CHOICE.POSTPAID].includes(choice)
  )

  const isPaidCredit = choice === PAYMENT_CHOICE.CREDIT && isInvoicePaid

  if (isConsignmentOrPostpaid || isPaidCredit) {
    return PAYMENT_CHOICE.POD
  }

  return choice
}

const decorateTransaction = (transaction, { payments, shipment, plan, paymentsPending, isInstallment, invoice }) => {
  const status = getTransactionStatus(transaction)
  const isPaid = isTransactionPaid(transaction)
  const isInvoicePaid = isInvoiceTransaction(transaction) && isPaid
  const partiallyPaidAmount = getPartiallyPaidAmount(transaction)

  // If shipment has no paymentChoice and a plan exists, or if this is an installment,
  // force paymentChoice to CREDIT. Otherwise use shipment.paymentChoice
  let paymentChoiceRaw = get(shipment, 'paymentChoice')
  if ((!paymentChoiceRaw && plan) || isInstallment) {
    paymentChoiceRaw = PAYMENT_CHOICE.CREDIT
  }

  const paymentChoice = normalizePaymentChoice(paymentChoiceRaw, isInvoicePaid)

  const isPaymentPending = isInstallment
    ? isInstallmentPaymentPending(paymentsPending, transaction)
    : isInvoicePaymentPending(paymentsPending, transaction)

  const decoratedTransaction = {
    ...transaction,
    ...(invoice && { invoiceData: invoice }),
    status,
    isPaid,
    isPaymentPending,
    partiallyPaidAmount,
    shipmentData: shipment,
    paymentChoice
  }

  const getPaymentForInstallment = (payments) => (
    payments.filter(p => p.installmentNo === transaction.installmentNo)
  )

  if (payments && payments.length) {
    decoratedTransaction.paymentsData = isInstallment
      ? getPaymentForInstallment(payments)
      : payments
  }

  if (plan) decoratedTransaction.paymentPlanData = plan

  return addTransactionEvents(decoratedTransaction)
}

const decorateTransactionsWithPaymentPlan = ({
  plan,
  installments,
  paymentsByPlanId,
  paymentsPendingByPlanId,
  shipment,
  invoice
}) => {
  const planId = plan.paymentPlanId
  const planPayments = paymentsByPlanId[planId] || []
  const planPending = paymentsPendingByPlanId[planId] || []

  const relatedInstallments = installments.filter(i => i.paymentPlanId === planId)

  return relatedInstallments.map(inst =>
    decorateTransaction(inst, {
      payments: planPayments,
      shipment,
      plan,
      paymentsPending: planPending,
      isInstallment: true,
      invoice
    })
  )
}

const decorateInvoiceTransaction = ({
  invoice,
  shipment,
  payments,
  paymentsPending,
  invoiceLateFeesByFeeInvoiceId,
  installmentLateFeesByFeeInvoiceId,
  paymentPlanServiceFeesByFeeInvoiceId
}) => {
  const invoiceId = invoice.txnId
  const invoiceBalance = invoice.balance
  const isLateFee = isLateFeeInvoice(invoiceId, invoiceLateFeesByFeeInvoiceId, installmentLateFeesByFeeInvoiceId)
  const isServiceFee = isServiceFeeInvoice(invoiceId, paymentPlanServiceFeesByFeeInvoiceId)
  const isConverting = (
    get(shipment, 'paymentChoice') === PAYMENT_CHOICE.CREDIT && invoiceBalance !== 0 && !isLateFee && !isServiceFee
  )

  const invoiceBase = {
    ...invoice,
    ...(isLateFee && { isLateFee }),
    ...(isServiceFee && { isServiceFee }),
    ...(isConverting && { isConverting })
  }

  return decorateTransaction(invoiceBase, {
    payments,
    shipment,
    plan: null,
    paymentsPending,
    isInstallment: false
  })
}

const transformFinancialOverview = (data) => {
  const {
    customer: accountSummary = {},
    invoices = [],
    paymentPlans = [],
    installments = [],
    invoicePayments = [],
    installmentPayments = [],
    invoicePendingPayments = [],
    installmentPendingPayments = [],
    invoiceLateFees = [],
    installmentLateFees = [],
    paymentPlanServiceFees = [],
    shipments = []
  } = data

  const paymentPlansByInvoiceId = keyBy(paymentPlans, 'invoiceId')
  const shipmentsByInvoiceId = keyBy(shipments, 'invoiceId')
  const paymentsByInvoiceId = groupBy(invoicePayments, 'invoiceId')
  const paymentsByPlanId = groupBy(installmentPayments, 'paymentPlanId')
  const paymentsPendingByInvoiceId = groupBy(invoicePendingPayments, 'invoiceId')
  const paymentsPendingByPlanId = groupBy(installmentPendingPayments, 'paymentPlanId')
  const invoiceLateFeesByFeeInvoiceId = keyBy(invoiceLateFees, 'feeInvoiceId')
  const installmentLateFeesByFeeInvoiceId = keyBy(installmentLateFees, 'feeInvoiceId')
  const paymentPlanServiceFeesByFeeInvoiceId = keyBy(paymentPlanServiceFees, 'feeInvoiceId')

  const transactions = []

  for (const invoice of invoices) {
    const invoiceId = invoice.txnId
    const shipment = shipmentsByInvoiceId[invoiceId]
    const plan = paymentPlansByInvoiceId[invoiceId]
    const payments = paymentsByInvoiceId[invoiceId] || []
    const paymentsPending = paymentsPendingByInvoiceId[invoiceId] || []

    if (plan) {
      transactions.push(
        ...decorateTransactionsWithPaymentPlan({
          plan,
          installments,
          paymentsByPlanId,
          paymentsPendingByPlanId,
          shipment,
          invoice
        })
      )
    } else {
      transactions.push(
        decorateInvoiceTransaction({
          invoice,
          shipment,
          payments,
          paymentsPending,
          invoiceLateFeesByFeeInvoiceId,
          installmentLateFeesByFeeInvoiceId,
          paymentPlanServiceFeesByFeeInvoiceId
        })
      )
    }
  }

  // Payment plans that were created manually (no invoiceId).
  // Previously we didn't show them; now we decorate their installments
  // and add them to transactions. shipment and invoice are intentionally
  // undefined here (no invoiceId to link).
  for (const plan of paymentPlans) {
    if (!plan.invoiceId) {
      transactions.push(
        ...decorateTransactionsWithPaymentPlan({
          plan,
          installments,
          paymentsByPlanId,
          paymentsPendingByPlanId,
          shipment: undefined,
          invoice: undefined
        })
      )
    }
  }

  return {
    accountSummary,
    transactions,
    invoiceLateFees,
    installmentLateFees,
    paymentPlanServiceFees
  }
}

export const decoratedAccountSummary = (summary) => (
  summary ? {
    ...summary,
    isPaused: !!summary.isBlocked,
    availableCashBalance: Math.abs(summary.cashBalance),
    availableCreditBalance: Math.max(summary.creditBalance, 0)
  } : {}
)

export const getInvoiceDocNoLabel = (payable) => {
  const invoiceDocNo = get(payable, 'invoiceData.docNo') || get(payable, 'docNo')

  // No invoice — use last 4 chars of paymentPlanId (or empty if missing)
  if (!invoiceDocNo) {
    return (`${get(payable, 'paymentPlanId', '')}`).slice(-4)
  }

  const invoiceDocSuffix = invoiceDocNo.slice(-4)
  const isLateFee = get(payable, 'isLateFee')
  const isServiceFee = get(payable, 'isServiceFee')

  let invoiceType = 'INV'
  if (isLateFee) {
    invoiceType = 'LATE FEE'
  }
  if (isServiceFee) {
    invoiceType = 'SERVICE FEE'
  }

  return `${invoiceType} ${invoiceDocSuffix}`
}

export const getInstallmentLabel = (payable) => {
  const isFixedTermPayable = isFixedTermInstallment(payable)
  const installmentNo = get(payable, 'installmentNo')
  const numInstallments = get(payable, 'paymentPlanData.numInstallments')
  return isFixedTermPayable ? 'Fixed' : `${installmentNo} of ${numInstallments}`
}

export const getPayableLabel = (payable, useInstallmentText = false) => {
  const installmentNo = get(payable, 'installmentNo')
  const invoiceDocNoLabel = getInvoiceDocNoLabel(payable)
  const installmentLabel = installmentNo ? `(${getInstallmentLabel(payable)})` : ''
  return `${useInstallmentText ? 'Instalment' : invoiceDocNoLabel} ${installmentLabel}`
}

/**
 * Sorts an array of objects by multiple keys and directions.
 *
 * @param {Array} list - The array to sort.
 * @param {string[]} sortKeys - Keys or paths to sort by (e.g. ['dueDate', 'txnDate']).
 * @param {('asc'|'desc')[]} orders - Directions for each key (e.g. ['asc', 'asc']).
 * @returns {Array} A new sorted array.
 */
export const multiSort = (list, sortKeys = ['dueDate', 'txnDate'], orders = ['asc', 'asc']) => {
  if (!Array.isArray(list) || !list.length) return []
  if (!Array.isArray(sortKeys) || !sortKeys.length) return list

  // Map each key to an iteratee that extracts and parses values
  const iteratees = sortKeys.map((key) => {
    return (item) => {
      const value = get(item, key)
      const dateValue = Date.parse(value)
      return isNaN(dateValue) ? value : new Date(dateValue)
    }
  })

  return orderBy(list, iteratees, orders)
}

const categorizeByPaidStatus = (transactions) => {
  if (!transactions || !transactions.length) {
    return {
      paidPayables: [],
      unpaidPayables: []
    }
  }

  const [paidPayables, unpaidPayables] = partition(transactions, t => t.isPaid)
  return {
    paidPayables: multiSort(paidPayables, ['dueDate'], ['desc']),
    unpaidPayables
  }
}

const categorizeUnpaidByDueDate = (unpaidTransactions, currentDate = new Date()) => {
  if (!unpaidTransactions || !unpaidTransactions.length) {
    return {
      duePayables: [],
      futurePayables: []
    }
  }

  const [duePayables, futurePayables] = partition(unpaidTransactions, t => {
    const dueDate = new Date(t.dueDate)
    return dueDate <= currentDate
  })

  return {
    duePayables: multiSort(duePayables),
    futurePayables: multiSort(futurePayables)

  }
}

const getOverduePayables = (duePayables) => {
  return duePayables.filter(duePayable => duePayable.status === PAYMENT_STATUS.OVERDUE)
}

export const getCategorizedPayables = (payables) => {
  const { paidPayables, unpaidPayables } = categorizeByPaidStatus(payables)
  const { duePayables, futurePayables } = categorizeUnpaidByDueDate(unpaidPayables)
  const overduePayables = getOverduePayables(duePayables)

  return {
    paidPayables,
    unpaidPayables,
    duePayables,
    futurePayables,
    overduePayables
  }
}

/**
 * Oldest eligible *non-exception* payable id.
 * - Excludes: paid, pending, POD, and converting payables.
 * - Returns the txnId of the oldest remaining payable, or null.
 */
export const selectPayableToUnlockId = (payables = []) => {
  if (!payables || !payables.length) return null

  const ordered = multiSort(payables)
  const candidate = ordered.find(payable =>
    payable &&
    !payable.isPaid &&
    !payable.isPaymentPending &&
    (get(payable, 'paymentChoice') !== PAYMENT_CHOICE.POD || !!get(payable, 'isLateFee')) &&
    !get(payable, 'isConverting')
  )

  return candidate ? candidate.txnId : null
}

/**
 * Assigns `canMakePayment`:
 * - Never for paid (isPaid) or pending (isPaymentPending)
 * - Yes for any eligible POD payables
 * - Yes for any eligible converting payables (isConverting)
 * - Also yes for the oldest eligible payable (even if not POD / not converting)
 */
const assignCanMakePayment = (payables = []) => {
  const targetId = selectPayableToUnlockId(payables)

  return (payables || []).map((payable) => {
    if (!payable) return payable

    const eligible = !payable.isPaid && !payable.isPaymentPending
    const isPod = get(payable, 'paymentChoice') === PAYMENT_CHOICE.POD && !get(payable, 'isLateFee')
    const isConverting = !!get(payable, 'isConverting')
    const isOldestEligible = !!targetId && payable.txnId === targetId

    const canMakePayment = eligible && (isPod || isConverting || isOldestEligible)
    return { ...payable, canMakePayment }
  })
}

export const getTransformedFinancialOverview = (financialOverview) => {
  const {
    accountSummary,
    transactions: payables,
    invoiceLateFees,
    installmentLateFees,
    paymentPlanServiceFees
  } = transformFinancialOverview(financialOverview)

  const lateFees = [...invoiceLateFees, ...installmentLateFees]

  return {
    accountSummary: decoratedAccountSummary(accountSummary),
    payables: assignCanMakePayment(payables),
    serviceFees: paymentPlanServiceFees,
    lateFees
  }
}
